import { 
  doc, 
  updateDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';
import { notificationHelpers } from './notificationService';
import { uploadImageToCloudinary } from './cloudinaryConfig';

// Payment workflow service for Cash and E-wallet payments
export const paymentWorkflowService = {
  // Process Cash payment
  processCashPayment: async (paymentId, cashData, tenantUser) => {
    try {
      console.log('Processing cash payment:', { paymentId, cashData, tenantUser });
      
      // Get the current payment data
      const paymentRef = doc(db, 'payments', paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (!paymentDoc.exists()) {
        return {
          success: false,
          error: 'Payment not found'
        };
      }
      
      const paymentData = paymentDoc.data();
      const previousStatus = paymentData.status;
      
      // Update payment status to Processing
      await updateDoc(paymentRef, {
        status: 'Processing',
        paymentMethod: 'Cash',
        cashPaymentDetails: {
          amount: cashData.amount,
          date: cashData.date,
          reference: cashData.reference,
          remarks: cashData.remarks,
          submittedBy: tenantUser.id || tenantUser.uid,
          submittedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
      
      // Notify all admin users about the cash payment request
      const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminSnapshot = await getDocs(adminQuery);
      
      const notifications = [];
      for (const adminDoc of adminSnapshot.docs) {
        const adminData = adminDoc.data();
        const notification = await notificationHelpers.createPaymentNotification(
          adminData.uid,
          {
            id: paymentId,
            amount: cashData.amount,
            tenantName: tenantUser.displayName || tenantUser.email,
            status: 'Processing',
            paymentMethod: 'Cash',
            cashPaymentDetails: cashData,
            submittedBy: tenantUser.id || tenantUser.uid,
            tenantId: tenantUser.id || tenantUser.uid
          },
          'admin'
        );
        
        if (notification.success) {
          notifications.push(notification);
        }
      }
      
      console.log(`Created ${notifications.length} admin notifications for cash payment`);
      
      return {
        success: true,
        message: 'Cash payment submitted successfully. Admins have been notified.',
        notifications: notifications.length
      };
      
    } catch (error) {
      console.error('Cash payment processing error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  // Process E-wallet payment
  processEwalletPayment: async (paymentId, ewalletData, tenantUser) => {
    try {
      console.log('Processing e-wallet payment:', { paymentId, ewalletData, tenantUser });
      
      // Validate required fields
      if (!ewalletData.file) {
        return {
          success: false,
          error: 'Receipt image is required for e-wallet payments'
        };
      }
      
      // Upload receipt image to Cloudinary
      const uploadResult = await uploadImageToCloudinary(ewalletData.file);
      
      if (!uploadResult.success) {
        return {
          success: false,
          error: `Failed to upload receipt: ${uploadResult.error}`
        };
      }
      
      // Get the current payment data
      const paymentRef = doc(db, 'payments', paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (!paymentDoc.exists()) {
        return {
          success: false,
          error: 'Payment not found'
        };
      }
      
      const paymentData = paymentDoc.data();
      const previousStatus = paymentData.status;
      
      // Update payment status to Processing and save receipt URL
      await updateDoc(paymentRef, {
        status: 'Processing',
        paymentMethod: 'E-Wallet',
        ewalletPaymentDetails: {
          amount: ewalletData.amount,
          date: ewalletData.date,
          reference: ewalletData.reference,
          receiptUrl: uploadResult.url,
          receiptPublicId: uploadResult.publicId,
          submittedBy: tenantUser.id || tenantUser.uid,
          submittedAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
      
      // Notify all admin users about the e-wallet payment request
      const adminQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminSnapshot = await getDocs(adminQuery);
      
      const notifications = [];
      for (const adminDoc of adminSnapshot.docs) {
        const adminData = adminDoc.data();
        const notification = await notificationHelpers.createPaymentNotification(
          adminData.uid,
          {
            id: paymentId,
            amount: ewalletData.amount,
            tenantName: tenantUser.displayName || tenantUser.email,
            status: 'Processing',
            paymentMethod: 'E-Wallet',
            ewalletPaymentDetails: {
              ...ewalletData,
              receiptUrl: uploadResult.url
            },
            submittedBy: tenantUser.id || tenantUser.uid,
            tenantId: tenantUser.id || tenantUser.uid
          },
          'admin'
        );
        
        if (notification.success) {
          notifications.push(notification);
        }
      }
      
      console.log(`Created ${notifications.length} admin notifications for e-wallet payment`);
      
      return {
        success: true,
        message: 'E-wallet payment submitted successfully with receipt. Admins have been notified.',
        notifications: notifications.length,
        receiptUrl: uploadResult.url
      };
      
    } catch (error) {
      console.error('E-wallet payment processing error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },
  
  // Get payment details for display
  getPaymentDetails: async (paymentId) => {
    try {
      const paymentRef = doc(db, 'payments', paymentId);
      const paymentDoc = await getDoc(paymentRef);
      
      if (!paymentDoc.exists()) {
        return {
          success: false,
          error: 'Payment not found'
        };
      }
      
      return {
        success: true,
        payment: {
          id: paymentDoc.id,
          ...paymentDoc.data()
        }
      };
      
    } catch (error) {
      console.error('Get payment details error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}; 